import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { Box, Button, Hidden, IconButton } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { useMutation } from "@tanstack/react-query"

import { ActionDrawer } from "../../../../../../components/ActionDrawer"
import { CustomePanel, CustomeTab } from "../../../../../../components/customeTab"
import Loader from "../../../../../../components/loader"
import StatusComponent from "../../../../../../components/status"
import Visite from "../visite"
import { BosySecton } from "../bodySection"
import { Section } from "../section"
import { useStyles } from "./styles"
import { DetailVisitProps } from "./types"

import { emitEvent } from "../../../../../../hooks/emitter"
import { EventQuery, Events, queryClient } from "../../../../../../hooks/emitter/types"
import { getStatus } from "../../../../../../hooks/status"
import { useContextState } from "../../../../../../hooks/theme"
import { loogbookServiceFetch, userScheduleServiceFetch } from "../../../../../../utils/constants/callService"
import { Status, Types } from "../../../../../../utils/enums"
import { TypeOfQuestionarie } from "../../../../../../utils/enums/typeQuestionarie"
import { icons } from "../../../../../../utils/icons"

import { ActivityForm } from "../../../../../logbook/pages/main/components/shared/formActivity/create/components/activityForm"
import { useInput } from "../../../../../logbook/pages/main/components/shared/formActivity/create/hooks/useInput"
import { useBody } from "../../../../../logbook/pages/main/components/shared/formActivity/create/hooks/body"
import { Roles } from "../../../../../../utils/enums"
import { getUserRole } from "@/service/auth"
import { getLocalStorage } from "@/utils/crud/crypt"
import { User } from "@/components/layout/aside/types"
import { Constants } from "@/utils/constants"

type ScheduleOrigin = "USER" | "UNIT"

type Props = DetailVisitProps & {
  scheduleId?: string | number
  assignedQuestionnaireIds?: string
  userQuestionnaireIds?: string
  hasActivities?: boolean
  isScheduleUnity?: boolean
  questionnaireName?: string
}

const DetailVisit = ({
  data,
  bodySection,
  isStore,
  questionsData,
  visitList = [],
  saveReportUrl,
  seeDetail,
  logbookPrefilters,
  scheduleId,
  presetDestination,
  createUserId,
  assignedQuestionnaireIds,
  userQuestionnaireIds,
  hasActivities,
  isScheduleUnity,
  questionnaireName,
}: Props) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { colors } = useContextState()
  const styles = useStyles()
  const { t } = useTranslation()

  const role = Number(getUserRole())
  const localStorage: User = getLocalStorage()
  const forceMyActivityTab = role !== Roles.ADMIN && Number(createUserId) === localStorage.id

  const [openActivityForm, setOpenActivityForm] = useState(false)

  const {
    filter,
    openField: typeActivityOpen,
    inputActivityCheck,
    setInputActivity,
    inputDestination,
    inputActivity,
    inputFilterUnity,
    filterUnity,
    formatFilter,
    formatFilterUnity,
  } = useInput()

  const {
    destinatioCreated,
    editActivityDestination,
    localRegister,
    setLocalRegister,
    companies,
    tableUnits,
    handleRowClickUnity,
    setUnityToDestination,
    loading,
    totalPages,
    destination,
    setDestination,
    destinationFinal,
    setDestinationFinal,
  } = useBody(filter, formatFilter, formatFilterUnity, filterUnity)

  const [activities, setActivities] = useState<any[]>([])
  const validation = (localStorage.accessPermissionsActivities?.access_web == 1) || localStorage.role == Roles.ADMIN

  const handleActivities = (activity: any) => setActivities((prev) => [...prev, activity])
  const deleteActivity = (activityId: string) => setActivities((prev) => prev.filter((a) => a.id !== activityId))
  const editActivity = (activity: any, activityId: string) =>
    setActivities((prev) => prev.map((a) => (a.id === activityId ? activity : a)))

  const emits = useMemo(
    () => ({
      success: ({ message }: { message: string }) =>
        emitEvent(Events.snackbar, { open: true, type: Types.SUCCESS, message }),
      error: ({ message }: { message: string }) =>
        emitEvent(Events.snackbar, { open: true, type: Types.ERROR, message }),
      info: ({ message }: { message: string }) => emitEvent(Events.snackbar, { open: true, type: Types.INFO, message }),
    }),
    []
  )

  const buildLogbookPrefilters = () => {
    if (isScheduleUnity) {
      return {
        ...(logbookPrefilters ?? {}),
        assignedQuestionnaireIds,
      }
    }

    const pre: any = { ...(logbookPrefilters ?? {}) }
    if (pre?.storeId) delete pre.storeId

    return {
      ...pre,
      userQuestionnaireIds,
    }
  }

  const buildScheduleMeta = () => {
    const origin: ScheduleOrigin = isScheduleUnity ? "UNIT" : "USER"

    return {
      origin,
      scheduleId: scheduleId,
      questionnaireName: questionnaireName ?? "",
      assignedQuestionnaireIds: origin === "UNIT" ? assignedQuestionnaireIds : undefined,
      userQuestionnaireIds: origin === "USER" ? userQuestionnaireIds : undefined,
    }
  }

  const navigateToLogbook = () => {
    navigate("/dashboard/logbook", {
      state: {
        preFilters: buildLogbookPrefilters(),
        scheduleMeta: buildScheduleMeta(),
        forceMyActivityTab,
      },
    })
  }

  const handleOpenCreateActivity = () => {
    if (presetDestination?.id) {
      setDestinationFinal(presetDestination)
      setDestination(presetDestination)
    }
    setOpenActivityForm(true)
  }

  const handleSeeActivities = (e: any) => {
    if (!e) return

    if (hasActivities === false) {
      emits.info({ message: t("schedule.alerts.noActivities") })
      return
    }

    navigateToLogbook()
  }

  const statusLocale = getStatus(data.status == Status.CLOSE && isStore ? Status.CANCEL : data.status, colors, t)

  const closeQuestionnaire = useMemo(
    () => [Status.TO_BE_STARTED, Status.STARTED, Status.PAUSE, Status.EXPIRED, Status.INCOMPLETE].includes(bodySection.status),
    [bodySection.status]
  )

  const closeSchedule = useMutation({
    mutationFn: userScheduleServiceFetch.closeUserSchedule,
    onSuccess: (res) => {
      if (res?.errors?.length) {
        emits.error({ message: t("schedule.closeQuestionnaire.errorSchedule") })
        return
      }
      queryClient.invalidateQueries({ queryKey: [EventQuery.user_schedule], type: "active" })
      emits.success({ message: t("schedule.closeQuestionnaire.successSchedule") })
    },
    onError: () => emits.error({ message: t("schedule.closeQuestionnaire.errorSchedule") }),
  })

  const closeScheduleQuestionnaire = ({ id }: { id?: string }) => {
    emitEvent(Events.modal, {
      open: true,
      title: t("schedule.modal.wantCloseSchedule") ?? "",
      description: t("schedule.modal.descriptionCloseSchedule") ?? "",
      cancel: (payload) => payload?.close(),
      confirm: (payload) => {
        if (id) closeSchedule.mutate({ id })
        payload?.close()
      },
    })
  }

  const createActivities = useMutation({
    mutationFn: loogbookServiceFetch.createActivitiesToAllUsers,
    onSuccess: (res: any) => {
      if (res?.errors?.length) {
        emits.error({ message: t("schedule.createActivities.error") })
        return
      }
      emits.success({ message: t("schedule.createActivities.success") })
      setOpenActivityForm(false)
      setActivities([])
      navigateToLogbook()
    },
    onError: () => emits.error({ message: t("general.error") }),
  })

  const buildCreateBody = () => {
    const userAssign = Number(createUserId)

    const payload: any = {
      user_assign: userAssign,
      data: activities.map((activity: any) => {
        const item: any = {
          idActivity: activity?.id != null ? String(activity.id) : undefined,
          title: activity?.name?.trim() || undefined,
          dateEnd: activity.endDate?.format(Constants.dayjsFormat3),
          dateStart: activity.startDate?.format(Constants.dayjsFormat3),
          typeActivityId: activity?.type ? Number(activity.type.value) : undefined,
          typeActivityLabel: activity?.type?.label ?? undefined,
          locationName: activity?.destination?.direction ?? undefined,
          latitude: activity?.destination?.latitude != null ? String(activity.destination.latitude) : undefined,
          longitude: activity?.destination?.longitude != null ? String(activity.destination.longitude) : undefined,
          storeId: activity?.destination?.id ?? undefined,
          typeDestination: activity?.destination?.type ?? undefined,
          timeZone: activity.destination.timeZone?.code ?? undefined,
          activityTypeText: activity?.open ?? undefined,
          saveInPlaces: activity.destination.toCreate,
          placeId: activity.destination.toCreateSelected ? activity.destination.idDestnation : null,
          namePlace: activity.destination.name,
          companyId: activity.destination.compnayId ?? null
        }

        if (isScheduleUnity && assignedQuestionnaireIds) {
          item.model = "assignedQuestionnaire"
          item.modelId = assignedQuestionnaireIds
        }

        if (!isScheduleUnity && userQuestionnaireIds) {
          item.model = "userQuestionnaire"
          item.modelId = userQuestionnaireIds
        }

        return item
      }),
    }

    return [payload]
  }

  const validateBeforeCreate = () => {
    const userAssign = Number(createUserId)
    if (!userAssign) return false
    if (!activities.length) return false

    const invalid = activities.some((a: any) => {
      const hasTitle = Boolean(a?.name && String(a.name).trim().length >= 1)
      //const hasDest = Boolean(a?.destination?.id || a?.destination?.idDestnation)
      return !hasTitle /* || !hasDest */
    })
    console.log({ invalid, activities })

    return !invalid
  }

  const assignActivities = () => {
    if (!validateBeforeCreate()) {
      return
    }

    emitEvent(Events.modal, {
      open: true,
      title: t("schedule.modalActivity.createActivitiesTitle") ?? "",
      description: t("schedule.modalActivity.createActivitiesDescription") ?? "",
      cancel: (payload) => payload?.close(),
      confirm: (payload) => {
        createActivities.mutate(buildCreateBody())
        payload?.close()
      },
    })
  }

  return (
    <>
      <Hidden xlUp>
        <ActionDrawer body={<BosySecton {...bodySection} saveReportUrl={saveReportUrl} />}>
          {({ openDrawer }) => (
            <Visite
              {...data}
              detail
              children={<IconButton onClick={openDrawer}>{icons("setting")}</IconButton>}
              visitList={visitList}
              seeDetail={seeDetail}
            />
          )}
        </ActionDrawer>
      </Hidden>

      <Hidden xlDown>
        <Visite {...data} detail visitList={visitList} seeDetail={seeDetail} />
      </Hidden>

      <Box>
        <CustomeTab
          listTab={[{ id: "1", label: t("schedule.detail.questionnaireExecution"), value: "1" }]}
          value="1"
          endChildren={
            <Box sx={styles.setting}>
              <StatusComponent color={statusLocale.color} msg={statusLocale.text} />

              {!!closeQuestionnaire && bodySection?.typeOfQuestionnaire == TypeOfQuestionarie.BYUSER && (
                <LoadingButton
                  loading={closeSchedule.isPending}
                  onClick={() => closeScheduleQuestionnaire({ id })}
                  variant="container_error"
                  color="error"
                >
                  {t("general.closeScheduleUser")}
                </LoadingButton>
              )}
            </Box>
          }
        >
          <CustomePanel value="1">
            {validation &&
              <Box sx={styles.btnContainer}>
                <Button variant="contained" onClick={(e) => handleSeeActivities(e)}>
                  {t("schedule.detail.seeActivities")}
                </Button>

                <Button variant="contained" onClick={handleOpenCreateActivity}>
                  {t("schedule.detail.createActivity")}
                </Button>
              </Box>
            }

            {(!questionsData || questionsData.length === 0) && <Loader />}

            {questionsData?.map((question, index) => (
              <Section key={index} {...question} index={index} sectionOf={index + 1} totalSection={questionsData.length} />
            ))}
          </CustomePanel>
        </CustomeTab>
      </Box>

      <ActivityForm
        open={openActivityForm}
        setOpen={setOpenActivityForm}
        inputActivity={inputActivity}
        inputActivityCheck={inputActivityCheck}
        activities={activities}
        handleActivities={handleActivities}
        deleteActivity={deleteActivity}
        editActivity={editActivity}
        inputFilterUnity={inputFilterUnity}
        table={tableUnits as any}
        filterUnity={filterUnity as any}
        loading={loading as any}
        totalPages={totalPages}
        destination={destination}
        setDestination={setDestination as any}
        destinationFinal={destinationFinal}
        setDestinationFinal={setDestinationFinal as any}
        setUnityToDestination={setUnityToDestination as any}
        inputDestination={inputDestination}
        allUsers={false}
        assignActivities={assignActivities}
        handleRowClickUnity={handleRowClickUnity as any}
        setInputActivity={setInputActivity as any}
        typeActivityOpen={typeActivityOpen}
        companies={companies ?? []}
        editActivityDestination={editActivityDestination as any}
        localRegister={localRegister ?? []}
        setLocalRegister={setLocalRegister as any}
        destinatioCreated={destinatioCreated ?? []}
      />
    </>
  )
}

export { DetailVisit }
